import { useEffect, useState, useMemo } from 'react';
import { Plan } from 'screens/Plans/ConvertPlanModalV2/types';
import {
  CropZone as FormCropZone,
  RecordSourceType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import useMapCropZones from 'screens/Applications&WorkOrders/Components/hooks/useMapCropZones';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import {
  transformPlanCropZonesToOperationPropertyVO,
  transformPlanCropZonesToProperty
} from '../helpers';

interface UseCropZonesFromPlanParams {
  plan: Plan | undefined;
}

interface UseCropZonesFromPlanReturn {
  mappedCropZones: FormCropZone[];
  isMappingCropZones: boolean;
  isPropertiesLoading: boolean;
}

const useCropZonesFromPlan = ({
  plan
}: UseCropZonesFromPlanParams): UseCropZonesFromPlanReturn => {
  const [mappedCropZones, setMappedCropZones] = useState<FormCropZone[]>([]);
  const [isMappingCropZones, setIsMappingCropZones] = useState(false);
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();

  const {
    propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    fetchCropZoneSeasons,
    buildCropZoneMap,
    mapCropZones,
    isPropertiesMapLoading: isPropertiesLoading
  } = useMapCropZones();

  // Transform plan crop zones to the formats needed by the mapping hook
  const operationPropertyVOs = useMemo(() => {
    if (!plan || !propertyLandingPageData) {
      return [];
    }
    return transformPlanCropZonesToOperationPropertyVO(
      plan,
      propertyLandingPageData,
      fieldsAndAreasGeoJSONCollection
    );
  }, [plan, propertyLandingPageData, fieldsAndAreasGeoJSONCollection]);

  const properties = useMemo(() => {
    if (!plan) {
      return [];
    }
    return transformPlanCropZonesToProperty(plan);
  }, [plan]);

  // Map crop zones when data is available
  useEffect(() => {
    if (
      !plan?.id ||
      !operationPropertyVOs.length ||
      !properties.length ||
      !plan.cropSeasonIds?.length
    ) {
      setMappedCropZones([]);
      return;
    }

    const mapCropZonesData = async () => {
      setIsMappingCropZones(true);
      try {
        const cropSeasonIdsToUse = plan.cropSeasonIds.filter(id =>
          selectedCropSeasonIds.includes(id)
        );
        const cropZoneSeasonsMap = await fetchCropZoneSeasons(
          cropSeasonIdsToUse
        );
        const cropZoneMapProp = buildCropZoneMap(properties);
        const mapped = mapCropZones(
          cropZoneMapProp,
          cropZoneSeasonsMap,
          operationPropertyVOs,
          RecordSourceType.PLANS
        );
        setMappedCropZones(mapped);
      } catch (error) {
        setMappedCropZones([]);
        // eslint-disable-next-line no-console
        console.error('Error mapping crop zones from plan:', error);
      } finally {
        setIsMappingCropZones(false);
      }
    };

    mapCropZonesData();
  }, [
    plan,
    operationPropertyVOs,
    properties,
    fetchCropZoneSeasons,
    buildCropZoneMap,
    mapCropZones,
    selectedCropSeasonIds
  ]);

  return {
    mappedCropZones,
    isMappingCropZones,
    isPropertiesLoading
  };
};

export default useCropZonesFromPlan;
